import { Suspense, lazy, useEffect } from "react";
import { RouterProvider } from "react-router-dom";

import Dialogs from "@/components/Core/Dialogs";
import PromotionSync from "@/components/Core/PromotionSync";
import WalletSync from "@/components/Core/WalletSync";
import CookieConsentBanner from "@/components/Others/CookieConsent";
import GeoBlockNotice from "@/components/Others/GeoBlockNotice";
import RealityCheck from "@/components/Others/RealityCheck";
import SupportWidget from "@/components/Others/SupportWidget";
import { DialogProvider } from "@/context/DialogProvider";
import { SocketProvider } from "@/context/SocketProvider";
import { TelegramProvider } from "@/context/TelegramProvider";
import { ChatRoomGameProvider, useIsLoggedIn, useSoundEngine } from "@/hooks";
import { trackPageView } from "@/lib/analytics";
import { refreshPageMeta } from "@/lib/pageMeta";
import "@/locales/i18n";
import routes from "@/routes";

import { ThemedToastContainer } from "./components/Core/ThemedToastContainer";

// Signed-in players only; its forms reuse the sign-in fields, which stay out of a guest's first load.
const AccountCompletion = lazy(() => import("@/components/Others/AccountCompletion"));

export default function App() {
  const isLoggedIn = useIsLoggedIn();

  // The games play through a module-level audio engine; this is what keeps it
  // in step with the level the player set and wakes it on their first press.
  useSoundEngine();

  // Canonical and the private-path noindex follow every navigation, pages with no meta of their own too.
  useEffect(() => routes.subscribe(() => refreshPageMeta()), []);

  // One page view per path; query-only changes (tabs, filters) are the same page.
  useEffect(() => {
    let lastPath = routes.state.location.pathname;
    trackPageView(lastPath);
    return routes.subscribe(({ location }) => {
      if (location.pathname === lastPath) return;
      lastPath = location.pathname;
      trackPageView(lastPath);
    });
  }, []);

  return (
    <SocketProvider>
      <DialogProvider>
        <TelegramProvider>
          <ChatRoomGameProvider>
            <RouterProvider router={routes} />
            <ThemedToastContainer />
            <Dialogs />
            <GeoBlockNotice />
            <PromotionSync />
            {isLoggedIn && <WalletSync />}
            {isLoggedIn && (
              <Suspense fallback={null}>
                <AccountCompletion />
              </Suspense>
            )}
            <CookieConsentBanner />
            <RealityCheck />
            <SupportWidget />
          </ChatRoomGameProvider>
        </TelegramProvider>
      </DialogProvider>
    </SocketProvider>
  );
}
